import { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/SiteChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [busy, setBusy] = useState(false);
  const { applySession } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/api/auth/admin/login", form);
      applySession(data);
      toast.success("Welcome back", { description: "Administrator session started" });
      navigate(data.next || "/admin", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 py-16">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-start gap-4">
          <Logo />
          <div>
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-emerald-400">
              <ShieldCheck className="h-3.5 w-3.5" /> Administrator portal
            </p>
            <h1 className="mt-3 font-display text-3xl font-bold text-white sm:text-4xl">Staff sign in</h1>
            <p className="mt-2 text-sm text-slate-400">
              Restricted to CARENEST administrators. Every attempt is logged.
            </p>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="space-y-5 rounded-2xl border border-slate-800 bg-slate-900/80 p-6 shadow-2xl backdrop-blur"
          data-testid="admin-login-form"
        >
          <div>
            <Label htmlFor="identifier" className="text-slate-300">Work email</Label>
            <Input
              id="identifier"
              required
              autoComplete="username"
              value={form.identifier}
              onChange={(e) => setForm({ ...form, identifier: e.target.value })}
              className="mt-1.5 border-slate-700 bg-slate-950 text-white placeholder:text-slate-600"
              data-testid="admin-login-email-input"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-slate-300">Password</Label>
            <PasswordInput
              id="password"
              required
              autoComplete="current-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-1.5 border-slate-700 bg-slate-950 text-white"
              testId="admin-login-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="w-full rounded-full bg-emerald-500 text-slate-950 hover:bg-emerald-400"
            data-testid="admin-login-submit-btn"
          >
            {busy ? "Verifying…" : "Sign in securely"}
          </Button>
          <p className="text-center text-xs text-slate-500" data-testid="admin-login-note">
            Looking for your family or practitioner account? Use the main sign-in page.
          </p>
        </form>
      </div>
    </div>
  );
}
